import React, { useState, useRef, useEffect } from 'react';
import { useFlowise } from '../context/FlowiseContext';
import IconButton from '@mui/material/IconButton';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import { PRIMARY_COLOR } from '../theme';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const lightenHex = (hex: string, percent: number) => {
  const normalized = hex.replace('#', '');
  const num = parseInt(normalized, 16);
  let r = (num >> 16) + Math.round((255 - (num >> 16)) * (percent / 100));
  let g = ((num >> 8) & 0x00FF) + Math.round((255 - ((num >> 8) & 0x00FF)) * (percent / 100));
  let b = (num & 0x0000FF) + Math.round((255 - (num & 0x0000FF)) * (percent / 100));
  r = Math.min(255, r);
  g = Math.min(255, g);
  b = Math.min(255, b);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

export const FlowiseBubble: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { query } = useFlowise();

  const BOT_COLOR = PRIMARY_COLOR;
  const BOT_COLOR_VERY_LIGHT = lightenHex(BOT_COLOR, 82);
  const LOADING_USER_BG = lightenHex("#F2F2F2", 50);
  const USER_DEFAULT_BG = 'radial-gradient(ellipse 109.00000000000001% 109.00000000000001% at 3% 50%, #FFFFFFFA 0%,#E2F0FF 100%)';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg = { from: 'user', text: input };
    setMessages(m => [...m, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const result = await query({ question: trimmed });

      let botText: string;
      if (!result) {
        botText = 'No response';
      } else if (typeof result === 'string') {
        botText = result;
      } else if (result.text) {
        botText = result.text;
      } else if (result.message) {
        botText = result.message;
      } else {
        try {
          botText = JSON.stringify(result);
        } catch {
          botText = String(result);
        }
      }

      const botMsg = { from: 'bot', text: botText };
      setMessages(m => [...m, botMsg]);

      // announce to screen readers
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = botText;
      }
    } catch (err: any) {
      const errMsg = { from: 'bot', text: err.message };
      setMessages(m => [...m, errMsg]);
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = err.message;
      }
    } finally {
      setIsLoading(false);
    }
  };

  // handle Enter to send, Shift+Enter newline
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // scroll to bottom whenever messages change or open changes
  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      setTimeout(() => {
        el.scrollTop = el.scrollHeight;
      }, 50);
    }
  }, [messages, open, isLoading]);

  // small layout/typography styles matching app theme
  const typographyStyle: React.CSSProperties = {
    fontFamily: "'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: 14,
    lineHeight: '1.45',
  };

  return (
    <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 9999 }}>
      {/* Live region for screen readers */}
      <div ref={liveRegionRef} role="status" aria-live="polite" aria-atomic="true" style={{ position: 'absolute', left: -9999, top: 'auto', width: 1, height: 1, overflow: 'hidden' }} />

      {!open && (
        <IconButton aria-label="Open chat" onClick={() => setOpen(true)} size="large" style={{ borderRadius: 24, width: 56, height: 56, background: BOT_COLOR_VERY_LIGHT, color: BOT_COLOR }}>
          <ChatBubbleOutlineIcon />
        </IconButton>
      )}

      {open && (
        <div style={{ width: 360, height: 520, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: 12, background: '#fff', display: 'flex', flexDirection: 'column', ...typographyStyle }}>
          <div style={{ padding: 12, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: BOT_COLOR, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ChatBubbleOutlineIcon style={{ color: '#fff' }} />
              <strong style={{ color: '#fff', fontSize: 15 }}>Docu Assistant</strong>
            </div>
            <IconButton aria-label="Close chat" onClick={() => setOpen(false)} size="small" style={{ background: BOT_COLOR, color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </div>

          <div ref={containerRef} style={{ flex: 1, overflow: 'auto', padding: 12 }} aria-live="off" role="log">
            {messages.map((m, i) => {
              const isLastUser = isLoading && m.from === 'user' && i === messages.length - 1;
              const background = m.from === 'user' ? (isLastUser ? LOADING_USER_BG : USER_DEFAULT_BG) : "inherit";
              const align = m.from === 'user' ? 'flex-end' : 'flex-start';

              return (
                <div key={i} style={{ display: 'flex', justifyContent: align, marginBottom: 10 }}>
                  <div style={{ maxWidth: '78%' }}>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 6, textTransform: 'capitalize' }}>{m.from}</div>
                    <div style={{ background, color: '#000', padding: '10px 12px', borderRadius: 0, whiteSpace: 'pre-wrap',  }}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener noreferrer" style={{ color: BOT_COLOR }} />,
                          p: ({ node, ...props }) => <p style={{ margin: 0, color: '#000' }} {...props} />,
                          li: ({ node, ...props }) => <li style={{ color: '#000' }} {...props} />
                        }}
                      >
                        {m.text}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ padding: 12, borderTop: '1px solid #eee', display: 'flex', gap: 8, alignItems: 'center' }}>
            <textarea
              aria-label="Type your question"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your question (Enter to send, Shift+Enter for newline)"
              disabled={isLoading}
              style={{ flex: 1, marginRight: 8, padding: 10, borderRadius: 6, border: '1px solid #ddd', minHeight: 44, resize: 'vertical', fontSize: 14, fontFamily: typographyStyle.fontFamily }}
            />
            <IconButton onClick={send} aria-label="Send message" style={{ background: BOT_COLOR, color: '#fff' }} disabled={isLoading || input.trim() === ''}>
              {isLoading ? <CircularProgress size={20} style={{ color: '#fff' }} /> : <SendIcon />}
            </IconButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowiseBubble;
